/**
 * Technical Debt Tracker Utilities
 */

import {
  TechnicalDebt,
  DebtCategory,
  BusinessImpact,
  ExtendedArchiMateDiagram,
} from './extended-types';
import { ArchiMateElement } from './types';
import { uuidv7 } from 'uuidv7';

export function createTechnicalDebt(
  description: string,
  category: DebtCategory,
  score: number,
  businessImpact: BusinessImpact,
  estimatedEffort: string,
  estimatedCost?: number,
  currency?: string
): TechnicalDebt {
  return {
    id: uuidv7(),
    score,
    category,
    description,
    estimatedEffort,
    estimatedCost,
    currency: currency || 'USD',
    businessImpact,
    linkedIssues: [],
    addedDate: new Date().toISOString().split('T')[0],
    priority: calculateDebtPriority(score, businessImpact),
  };
}

export function calculateDebtPriority(
  score: number,
  businessImpact: BusinessImpact
): number {
  const impactWeight = {
    low: 1,
    medium: 2,
    high: 3,
    critical: 4,
  }[businessImpact];

  // Priority is combination of score and impact (1-5 scale)
  const priority = Math.min(5, Math.ceil((score * impactWeight) / 10));
  return priority;
}

export function calculateDiagramDebtScore(diagram: ExtendedArchiMateDiagram): number {
  let totalDebt = 0;
  let count = 0;

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt) {
      totalDebt += debt.score;
      count++;
    }
  });

  return count > 0 ? totalDebt / count : 0;
}

export function getElementsByDebtCategory(
  diagram: ExtendedArchiMateDiagram,
  category: DebtCategory
): Array<{ element: ArchiMateElement; debt: TechnicalDebt }> {
  const elements: Array<{ element: ArchiMateElement; debt: TechnicalDebt }> = [];

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt && debt.category === category) {
      elements.push({ element, debt });
    }
  });

  return elements;
}

export function getHighPriorityDebt(
  diagram: ExtendedArchiMateDiagram,
  minPriority: number = 4
): Array<{ element: ArchiMateElement; debt: TechnicalDebt }> {
  const highPriority: Array<{ element: ArchiMateElement; debt: TechnicalDebt }> = [];

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt && debt.priority >= minPriority) {
      highPriority.push({ element, debt });
    }
  });

  return highPriority.sort((a, b) => b.debt.priority - a.debt.priority);
}

export function getDebtByBusinessImpact(
  diagram: ExtendedArchiMateDiagram,
  impact: BusinessImpact
): Array<{ element: ArchiMateElement; debt: TechnicalDebt }> {
  const elements: Array<{ element: ArchiMateElement; debt: TechnicalDebt }> = [];

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt && debt.businessImpact === impact) {
      elements.push({ element, debt });
    }
  });

  return elements;
}

export function getOverdueDebt(
  diagram: ExtendedArchiMateDiagram
): Array<{ element: ArchiMateElement; debt: TechnicalDebt; daysOverdue: number }> {
  const overdue: Array<{
    element: ArchiMateElement;
    debt: TechnicalDebt;
    daysOverdue: number;
  }> = [];
  const now = Date.now();

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt && debt.targetResolutionDate) {
      const targetDate = new Date(debt.targetResolutionDate).getTime();
      if (now > targetDate) {
        const daysOverdue = Math.floor((now - targetDate) / (1000 * 60 * 60 * 24));
        overdue.push({ element, debt, daysOverdue });
      }
    }
  });

  return overdue.sort((a, b) => b.daysOverdue - a.daysOverdue);
}

export function getDebtHeatmapData(diagram: ExtendedArchiMateDiagram): Record<string, number> {
  const heatmap: Record<string, number> = {};

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    heatmap[element.id] = debt ? debt.score : 0;
  });

  return heatmap;
}

export function getDebtColor(score: number): string {
  if (score === 0) return '#10b981'; // green
  if (score <= 3) return '#84cc16'; // lime
  if (score <= 5) return '#eab308'; // yellow
  if (score <= 7) return '#f97316'; // orange
  return '#ef4444'; // red
}

export function generateDebtReport(diagram: ExtendedArchiMateDiagram): string {
  let report = `# Technical Debt Report\n\n`;
  report += `## ${diagram.name}\n\n`;
  report += `**Generated:** ${new Date().toISOString().split('T')[0]}\n\n`;

  // Overall score
  const avgScore = calculateDiagramDebtScore(diagram);
  report += `## Overall Debt Score: ${avgScore.toFixed(1)}/10\n\n`;

  // Debt by category
  report += `## Debt by Category\n\n`;
  const categories: DebtCategory[] = [
    'legacy',
    'security',
    'performance',
    'maintainability',
    'testing',
    'documentation',
  ];

  categories.forEach((category) => {
    const elements = getElementsByDebtCategory(diagram, category);
    if (elements.length > 0) {
      const totalScore = elements.reduce((sum, { debt }) => sum + debt.score, 0);
      const avgCategoryScore = totalScore / elements.length;
      report += `### ${category.charAt(0).toUpperCase() + category.slice(1)}\n`;
      report += `- **Elements affected:** ${elements.length}\n`;
      report += `- **Average score:** ${avgCategoryScore.toFixed(1)}/10\n\n`;
    }
  });

  // High priority debt
  report += `## High Priority Debt (4-5 stars)\n\n`;
  const highPriority = getHighPriorityDebt(diagram);
  if (highPriority.length === 0) {
    report += `No high priority debt items.\n`;
  } else {
    highPriority.forEach(({ element, debt }) => {
      report += `### ${element.name} (Priority: ${'⭐'.repeat(debt.priority)})\n`;
      report += `- **Category:** ${debt.category}\n`;
      report += `- **Score:** ${debt.score}/10\n`;
      report += `- **Business Impact:** ${debt.businessImpact}\n`;
      report += `- **Estimated Effort:** ${debt.estimatedEffort}\n`;
      report += `- **Description:** ${debt.description}\n`;
      if (debt.linkedIssues.length > 0) {
        report += `- **Linked Issues:** ${debt.linkedIssues.join(', ')}\n`;
      }
      report += `\n`;
    });
  }

  // Overdue debt
  const overdue = getOverdueDebt(diagram);
  if (overdue.length > 0) {
    report += `## Overdue Debt\n\n`;
    overdue.forEach(({ element, debt, daysOverdue }) => {
      report += `- **${element.name}**: ${daysOverdue} days overdue\n`;
    });
    report += `\n`;
  }

  // Critical business impact
  const critical = getDebtByBusinessImpact(diagram, 'critical');
  if (critical.length > 0) {
    report += `## Critical Business Impact\n\n`;
    report += `${critical.length} items with critical business impact:\n\n`;
    critical.forEach(({ element, debt }) => {
      report += `- **${element.name}**: ${debt.description}\n`;
    });
  }

  return report;
}

export function estimateDebtRemediationCost(
  diagram: ExtendedArchiMateDiagram,
  hourlyRate: number = 150
): {
  totalHours: number;
  totalCost: number;
  byCategory: Record<DebtCategory, { hours: number; cost: number }>;
} {
  let totalHours = 0;
  const byCategory: Record<DebtCategory, { hours: number; cost: number }> = {
    legacy: { hours: 0, cost: 0 },
    security: { hours: 0, cost: 0 },
    performance: { hours: 0, cost: 0 },
    maintainability: { hours: 0, cost: 0 },
    testing: { hours: 0, cost: 0 },
    documentation: { hours: 0, cost: 0 },
  };

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt) {
      // Parse estimated effort (e.g., "2 weeks" → 80 hours)
      const hours = parseEffortToHours(debt.estimatedEffort);
      totalHours += hours;
      byCategory[debt.category].hours += hours;
      byCategory[debt.category].cost += hours * hourlyRate;
    }
  });

  const totalCost = totalHours * hourlyRate;

  return { totalHours, totalCost, byCategory };
}

function parseEffortToHours(effort: string): number {
  const match = effort.match(/(\d+)\s*(hour|day|week|month|sprint)/i);
  if (!match) return 0;

  const amount = parseInt(match[1]);
  const unit = match[2].toLowerCase();

  switch (unit) {
    case 'hour':
      return amount;
    case 'day':
      return amount * 8;
    case 'week':
      return amount * 40;
    case 'sprint':
      return amount * 80; // 2-week sprint
    case 'month':
      return amount * 160;
    default:
      return 0;
  }
}

export function prioritizeDebtRemediation(
  diagram: ExtendedArchiMateDiagram
): Array<{
  element: ArchiMateElement;
  debt: TechnicalDebt;
  priority: number;
  roi: number;
}> {
  const items: Array<{
    element: ArchiMateElement;
    debt: TechnicalDebt;
    priority: number;
    roi: number;
  }> = [];

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt) {
      // Calculate ROI: (business impact value - effort cost) / effort cost
      const impactValue = { low: 1, medium: 3, high: 5, critical: 10 }[debt.businessImpact];
      const effortHours = parseEffortToHours(debt.estimatedEffort);
      const roi = effortHours > 0 ? (impactValue * debt.score - effortHours) / effortHours : 0;

      items.push({ element, debt, priority: debt.priority, roi });
    }
  });

  // Sort by ROI descending (highest ROI first)
  return items.sort((a, b) => b.roi - a.roi);
}

export function exportDebtToCSV(diagram: ExtendedArchiMateDiagram): string {
  let csv =
    'Element,Category,Score,Priority,Business Impact,Estimated Effort,Description,Linked Issues,Added Date,Target Date\n';

  diagram.elements.forEach((element) => {
    const debt = diagram.elementMetadata[element.id]?.debt;
    if (debt) {
      csv += `"${element.name}",`;
      csv += `${debt.category},`;
      csv += `${debt.score},`;
      csv += `${debt.priority},`;
      csv += `${debt.businessImpact},`;
      csv += `"${debt.estimatedEffort}",`;
      csv += `"${debt.description.replace(/"/g, '""')}",`;
      csv += `"${debt.linkedIssues.join('; ')}",`;
      csv += `${debt.addedDate},`;
      csv += `${debt.targetResolutionDate || ''}\n`;
    }
  });

  return csv;
}
